import React from "react";
import FilterBar from "@/components/common/FilterBar";
import StickyFilterBar from "@/components/common/StickyFilterBar";
import { calculateDateRange, DATE_RANGE_KEYS, getRecalculatedDatesIfNeeded } from "@/utils/dateRangeUtils";
import { DateRangeChange, FilterChangeEvent } from "@/types";
import { useDashboardData } from "@/contexts/DashboardContext";
import { useFilterCache } from "@/hooks/useFilterCache";
import { useAppData, UserRole } from "@/contexts/AppDataContext";
import { getUnifiedChartHeight } from "@/lib/utils";
import SlaRankingWidget from "../widgets/agents/SlaRankingWidget";
import StatusOverTimeWidget from "../widgets/agents/StatusOverTimeWidget";
import AgentPerformanceTableWidget from "../widgets/agents/AgentPerformanceTableWidget";
import AgentPerformanceDetailTable from "../widgets/agents/AgentPerformanceDetailTable";
import { getAgentIds } from "@/utils/agentUtils";
import { EMPTY_QUEUE_ID, EMPTY_AGENT_ID } from "@/constants/queue";

const AGENT_LIMIT = 20;

const AgentsTab: React.FC<{ isGeneralMode?: boolean; filterClass?: string }> = ({ isGeneralMode, filterClass }) => {
  const defaultDateRangeKey = DATE_RANGE_KEYS.LAST_7_DAYS;
  const { current } = useDashboardData();
  const { filters, saveFilters } = useFilterCache("agent-performance-dashboard", current?.id);
  const [dateRangeKey, setDateRangeKey] = React.useState<string>(filters?.dateRangeKey ?? defaultDateRangeKey);
  const [searchTerm, setSearchTerm] = React.useState<string>(filters?.searchTerm ?? "");
  const [dateRange, setDateRange] = React.useState<DateRangeChange>({
    key: filters?.dateRangeKey ?? defaultDateRangeKey,
    startDate: filters?.startDate ?? null,
    endDate: filters?.endDate ?? null,
    previousStartDate: filters?.previousStartDate ?? null,
    previousEndDate: filters?.previousEndDate ?? null,
  });
  const { uniqueAgents, queuesDataVersion, userRole, queueMicrosoftIds } = useAppData();
  const isManager = userRole === UserRole.MANAGER;

  const agentIds = React.useMemo(() => getAgentIds(uniqueAgents), [uniqueAgents]);

  // TCX-6500: for a Manager, an unfiltered ("All Agents") request must still resolve to their
  // full in-scope agent list (never []); Admin/Partner keeps today's fallback (possibly []).
  //
  // TCX-fix-role: for a Manager, a manual pick is also clamped to their *current* authorized
  // scope (mirrors Queue tab's resolveScopedIds, TCX-6502) — a cached agent id from before a
  // role/queue change that removed them from the manager's scope must fall back to "All Agents"
  // for that scope instead of being sent verbatim.
  const getAgentMicrosoftIds = React.useCallback((agents: string[]): string[] | null => {
    if (isManager) {
      const authorized = new Set(agentIds);
      const clamped = agents.filter(id => authorized.has(id));
      if (clamped.length > 0) return clamped;
      return agentIds.length > 0 ? agentIds : [EMPTY_AGENT_ID];
    }
    if (agents.length > 0) return agents;
    return agentIds;
  }, [agentIds, isManager]);

  // TCX-6500: for a Manager, an unfiltered request must send their full assigned-queue scope
  // instead of `null` ("no restriction") — Admin/Partner keeps sending `null`, unchanged.
  //
  // TCX-fix-role: same clamp as getAgentMicrosoftIds above, for the queue selection.
  const resolveQueueMicrosoftIds = React.useCallback((selectedQueues: string[]): string[] | null => {
    if (!isManager) return null;
    const authorized = new Set(queueMicrosoftIds);
    const clamped = selectedQueues.filter(id => authorized.has(id));
    if (clamped.length > 0) return clamped;
    return queueMicrosoftIds.length > 0 ? queueMicrosoftIds : [EMPTY_QUEUE_ID];
  }, [isManager, queueMicrosoftIds]);

  // Current date parameters for widgets
  const [currentDateParams, setCurrentDateParams] = React.useState(() => {
    if (filters) {
      const { startDate, endDate, previousStartDate, previousEndDate } = getRecalculatedDatesIfNeeded(
        filters.dateRangeKey,
        filters
      );
      setDateRange({
        key: filters?.dateRangeKey,
        startDate,
        endDate,
        previousStartDate,
        previousEndDate,
      });
      return {
        startDate,
        endDate,
        previousStartDate,
        previousEndDate,
        queueMicrosoftIds: resolveQueueMicrosoftIds(filters.queues ?? []),
        agentMicrosoftIds: getAgentMicrosoftIds(filters.agents),
        groupIds: null as number[] | null,
        timeStamp: new Date().toISOString(),
      };
    }
    const dr = calculateDateRange(defaultDateRangeKey);
    return {
      startDate: dr.from.toISOString(),
      endDate: dr.to.toISOString(),
      previousStartDate: dr.previousFrom.toISOString(),
      previousEndDate: dr.previousTo.toISOString(),
      queueMicrosoftIds: resolveQueueMicrosoftIds([]),
      agentMicrosoftIds: getAgentMicrosoftIds([]),
      groupIds: null as number[] | null,
      timeStamp: new Date().toISOString(),
    };
  });

  // Reset state về mặc định khi filters bị reset/null (ví dụ sau khi reset cache)
  React.useEffect(() => {
    if (!filters) {
      const dr = calculateDateRange(defaultDateRangeKey);
      setDateRange({
        key: defaultDateRangeKey,
        startDate: dr.from.toISOString(),
        endDate: dr.to.toISOString(),
        previousStartDate: dr.previousFrom.toISOString(),
        previousEndDate: dr.previousTo.toISOString(),
      });
      setCurrentDateParams({
        startDate: dr.from.toISOString(),
        endDate: dr.to.toISOString(),
        previousStartDate: dr.previousFrom.toISOString(),
        previousEndDate: dr.previousTo.toISOString(),
        queueMicrosoftIds: resolveQueueMicrosoftIds([]),
        agentMicrosoftIds: getAgentMicrosoftIds([]),
        groupIds: null,
        timeStamp: new Date().toISOString(),
      });
      setSearchTerm("");
    }
  }, [filters, getAgentMicrosoftIds, resolveQueueMicrosoftIds]);

  // TCX-6500: userRole/queueMicrosoftIds/uniqueAgents load asynchronously after mount (the
  // with-agents-and-role poll). Recompute the resolved ids whenever that data changes so a
  // Manager isn't left on the pre-role-load `queueMicrosoftIds: null` default indefinitely.
  React.useEffect(() => {
    setCurrentDateParams(prev => ({
      ...prev,
      queueMicrosoftIds: resolveQueueMicrosoftIds(filters?.queues ?? []),
      agentMicrosoftIds: getAgentMicrosoftIds(filters?.agents ?? []),
    }));
  }, [resolveQueueMicrosoftIds, getAgentMicrosoftIds, filters?.queues, filters?.agents]);

  // TCX-6485 follow-up: agentMicrosoftIds now resyncs immediately when the roster changes (the
  // effect above), but a brand-new agent's call data can still lag behind while the data source's
  // cache warms up (Flow1/Flow2 backend timing) — the refetch that fires right after can
  // legitimately come back empty. Retry a few times, spaced out, on every real roster load —
  // NOT gated on detecting a before/after transition within this mount, because the common repro
  // is: add the agent, then open (or reload) the Users tab — the very first roster this mount
  // ever sees already contains the new agent, so there is no "before" to diff against. `agentIds`
  // only changes reference when `fetchQueuesWithRole` detects a real diff (see AppDataContext's
  // JSON-compare gate), so this does not refire on every 5s poll — only on an actual load/change.
  // Skips just the very first effect run while the roster is still the empty initial state (mount,
  // before the with-agents-and-role fetch resolves) — nothing to retry for yet at that point.
  // Coalesces to the latest roster change if another one lands mid-retry (cleanup clears the
  // pending interval).
  const isFirstAgentIdsRunRef = React.useRef(true);
  const NEW_AGENT_RETRY_INTERVAL_MS = 10000;
  const NEW_AGENT_RETRY_MAX_ATTEMPTS = 3;
  React.useEffect(() => {
    if (isFirstAgentIdsRunRef.current) {
      isFirstAgentIdsRunRef.current = false;
      if (agentIds.length === 0) return undefined; // mount, roster not loaded yet
    }

    let attempts = 0;
    const intervalId = setInterval(() => {
      attempts += 1;
      setCurrentDateParams(prev => ({ ...prev, timeStamp: new Date().toISOString() }));
      if (attempts >= NEW_AGENT_RETRY_MAX_ATTEMPTS) clearInterval(intervalId);
    }, NEW_AGENT_RETRY_INTERVAL_MS);
    return () => clearInterval(intervalId);
  }, [agentIds]);

  // Refresh data when tab becomes active, or when with-agents-and-role poll detects a real
  // change (skip initial mount — currentDateParams already has a fresh timeStamp)
  const isFirstActiveRef = React.useRef(true);
  React.useEffect(() => {
    if (isFirstActiveRef.current) {
      isFirstActiveRef.current = false;
      return;
    }
    if (isGeneralMode) {
      setCurrentDateParams(prev => ({
        ...prev,
        timeStamp: new Date().toISOString()
      }));
    }
  }, [isGeneralMode, queuesDataVersion]);

  const handleFilterChange = (event: FilterChangeEvent) => {
    setDateRangeKey(event.dateRange.key);
    
    // Recalculate dates for dynamic date ranges to ensure freshness
    const { startDate, endDate, previousStartDate, previousEndDate } = getRecalculatedDatesIfNeeded(
      event.dateRange.key,
      {
        dateRangeKey: event.dateRange.key,
        startDate: event.dateRange.startDate.toISOString(),
        endDate: event.dateRange.endDate.toISOString(),
        previousStartDate: event.dateRange.previousStartDate.toISOString(),
        previousEndDate: event.dateRange.previousEndDate.toISOString(),
      }
    );
    
    setDateRange({
      key: event.dateRange.key,
      startDate,
      endDate,
      previousStartDate,
      previousEndDate,
    });
    setCurrentDateParams({
      startDate,
      endDate,
      previousStartDate,
      previousEndDate,
      queueMicrosoftIds: resolveQueueMicrosoftIds(event.queues ?? []),
      agentMicrosoftIds: getAgentMicrosoftIds(event.agents),
      groupIds: null as number[] | null,
      timeStamp: new Date().toISOString(),
    });

    saveFilters({
      dateRangeKey: event.dateRange.key,
      startDate: event.dateRange.startDate.toISOString(),
      endDate: event.dateRange.endDate.toISOString(),
      previousStartDate: event.dateRange.previousStartDate.toISOString(),
      previousEndDate: event.dateRange.previousEndDate.toISOString(),
      queues: event.queues ?? [],
      agents: event.agents ?? [],
      selectedQueueType: event.selectedQueueType,
      searchTerm: searchTerm,
    });
  };

  // Update currentDateParams when filters change
  React.useEffect(() => {
    if (filters?.agents) {
      setCurrentDateParams(prev => ({
        ...prev,
        agentMicrosoftIds: getAgentMicrosoftIds(filters.agents || []),
        timeStamp: new Date().toISOString()
      }));
    }
  }, [filters?.agents, getAgentMicrosoftIds]);

  // Use ref to maintain stable reference for filters to prevent unnecessary rerenders
  const filtersRef = React.useRef(filters);
  React.useEffect(() => {
    filtersRef.current = filters;
  }, [filters]);

  const handleSearchChange = React.useCallback((newSearchTerm: string) => {
    setSearchTerm(newSearchTerm);

    const currentFilters = filtersRef.current;
    if (currentFilters) {
      saveFilters({
        ...currentFilters,
        searchTerm: newSearchTerm,
      });
    }
  }, [saveFilters]);

  // Calculate unified chart height for widgets
  const unifiedChartHeight = getUnifiedChartHeight(AGENT_LIMIT, AGENT_LIMIT);

  return (
    <div className="w-full">
      <div className="border text-primary bg-[#FCFCFD] rounded-lg dark:border-[#3C5C73] dark:bg-[#3C5C73] dark:text-white px-[8px] pb-[8px] pt-1">
        <StickyFilterBar className={filterClass}>
          <FilterBar
            onFilterChange={handleFilterChange}
            initialDateRangeKey={dateRangeKey}
            initialSelectedQueues={filters?.queues}
            initialSelectedAgents={filters?.agents}
            initialSelectedQueueType={filters?.selectedQueueType}
            initialDateRange={dateRange}
          />
        </StickyFilterBar>

        <div className="flex flex-col gap-4">
          <div>
            <AgentPerformanceDetailTable
              dateParams={{
                startDate: currentDateParams.startDate,
                endDate: currentDateParams.endDate,
                previousStartDate: currentDateParams.previousStartDate,
                previousEndDate: currentDateParams.previousEndDate,
                queueMicrosoftIds: currentDateParams.queueMicrosoftIds,
                agentMicrosoftIds: currentDateParams.agentMicrosoftIds,
                groupIds: currentDateParams.groupIds,
                timeStamp: currentDateParams.timeStamp,
              }}
              searchTerm={searchTerm}
              onSearchChange={handleSearchChange}
              limit={10}
            />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <SlaRankingWidget
              startDate={currentDateParams.startDate}
              endDate={currentDateParams.endDate}
              previousStartDate={currentDateParams.previousStartDate}
              previousEndDate={currentDateParams.previousEndDate}
              queueMicrosoftIds={currentDateParams.queueMicrosoftIds}
              agentMicrosoftIds={currentDateParams.agentMicrosoftIds}
              groupIds={currentDateParams.groupIds}
              unifiedChartHeight={unifiedChartHeight}
              agentLimit={AGENT_LIMIT}
            />
            <StatusOverTimeWidget
              startDate={currentDateParams.startDate}
              endDate={currentDateParams.endDate}
              previousStartDate={currentDateParams.previousStartDate}
              previousEndDate={currentDateParams.previousEndDate}
              queueMicrosoftIds={currentDateParams.queueMicrosoftIds}
              agentMicrosoftIds={currentDateParams.agentMicrosoftIds}
              groupIds={currentDateParams.groupIds}
              unifiedChartHeight={unifiedChartHeight}
              agentLimit={AGENT_LIMIT}
            />
          </div>

          <div>
            <AgentPerformanceTableWidget
              startDate={currentDateParams.startDate}
              endDate={currentDateParams.endDate}
              agentMicrosoftIds={currentDateParams.agentMicrosoftIds}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default AgentsTab;


